import clsx from 'clsx';
import React from 'react';

import type * as api from '@gitbook/api-client';
import { constructNodeSummary } from '@gitbook/doc-core';
import { isRevisionPageDocument, type RevisionPageDocument } from '@gitbook/hive-core';
import { useHiveRevision } from '@gitbook/hive-react';
import type { Document } from '@gitbook/slate';
import { Icon } from '@gitbook/spine-icons';

import { truncate } from '../../utils/text';
import { WithPageDocument } from '../Hive';
import { PageIcon } from '../Page';

/**
 * Displays a preview of a node or page for either an existing comment or before a comment is created (in the add-comment popover).
 */
export function CommentTargetPreview(props: {
    spaceId: string;
    pageId: string;
    tableCell: api.TableCellTarget | undefined;
    nodeKey: string | undefined;
}) {
    const { spaceId, pageId, tableCell, nodeKey } = props;
    const { revision } = useHiveRevision();

    const page = pageId ? revision.pages[pageId] : null;
    if (!page || !isRevisionPageDocument(page)) {
        return <DeleteParentQuotation parentType="page" />;
    }

    if (nodeKey) {
        if (nodeKey === 'page') {
            return <CommentPageTargetPreview page={page} />;
        }

        return (
            <WithPageDocument spaceId={spaceId} page={page}>
                {({ value }) => (
                    <CommentNodeTargetPreview
                        spaceId={spaceId}
                        pageId={page.id}
                        nodeKey={nodeKey}
                        tableCell={tableCell}
                        document={value.document}
                    />
                )}
            </WithPageDocument>
        );
    }
}

/**
 * Label with the current page's icon and title.
 */
function CommentPageTargetPreview(props: { page: RevisionPageDocument }) {
    const { page } = props;
    return (
        <Quotation className="whitespace-nowrap">
            <span className="flex items-center gap-2 rounded-md bg-muted px-2 py-0.5 font-[440] text-muted text-ui-standout">
                <PageIcon page={page} size={14} className="m-nudge-icon" />{' '}
                {truncate(page.title, 33)}
            </span>
        </Quotation>
    );
}

/**
 * Label with the current node's summary, if available.
 */
function CommentNodeTargetPreview(props: {
    spaceId: string;
    pageId: string;
    nodeKey: string;
    tableCell?: api.TableCellTarget;
    document: Document;
}) {
    const { nodeKey, tableCell, document } = props;

    const messageNodeSummary = React.useMemo(() => {
        if (document) {
            return constructNodeSummary(
                document,
                nodeKey,
                tableCell?.definition && tableCell.record
                    ? {
                          record: tableCell.record,
                          definition: tableCell.definition,
                      }
                    : undefined
            );
        }
        return null;
    }, [nodeKey, tableCell?.definition, tableCell?.record, document]);

    if (!messageNodeSummary) {
        return <DeleteParentQuotation parentType="block" />;
    }

    return <Quotation>{truncate(messageNodeSummary, 100)}</Quotation>;
}

function DeleteParentQuotation(props: { parentType: 'block' | 'page' }) {
    const { parentType } = props;
    return (
        <Quotation className="border-l-[var(--sp-ref-color-scarlet-300)] ">
            <span className="flex items-center gap-2 rounded bg-danger px-2 py-0.5 text-danger">
                <Icon.Trashcan size={12} className="m-nudge-icon" />
                This {parentType} was deleted
            </span>
        </Quotation>
    );
}

function Quotation(props: { children: React.ReactNode; className?: string }) {
    return (
        <span
            className={clsx(
                'mt-1.5 flex items-center gap-2 border-l-2 pl-2 text-light',
                props.className
            )}
        >
            {props.children}
        </span>
    );
}
